import "./LineUpList.css";

import { useMemo } from "react";

import {
  ISoccerMatchSubstituteEvent,
  ISoccerTeamOfficial,
  SoccerMatchEventType,
  SoccerPlayerPosition,
} from "../../../../types";
import substituteImage from "../../../public/images/match-events/substitute-new.svg";
import captainLogo from "../../assets/icons/captain.svg";
import ManagerIcon from "../../assets/icons/ManagerIcon";
import ShirtIcon from "../../assets/icons/Shirt";
import { IGroupedData, ISoccerMatchEventWithPlayerAction } from "../../hooks";
import {
  getDisplayPlayerInfo,
  getSubstituteDisplayInfo,
  IPlayers,
} from "../../utils";
import { POSITION_LABELS, POSITION_ORDER } from "../../utils";
import LineupMatchEvents from "./LineupMatchEvents/LineupMatchEvents";

interface LineupsListProps {
  players: IPlayers[];
  shirtColor: string;
  textColor: string;
  currentGroupedEventData: IGroupedData;
  teamOfficials: ISoccerTeamOfficial[];
}

interface ProcessedPlayerEvents {
  substituteEvent: ISoccerMatchSubstituteEvent[];
  otherEvents: ISoccerMatchEventWithPlayerAction[];
}

function isLightColor(color: string): boolean {
  if (!color) return false;
  const normalized = color.trim().toLowerCase();
  if (normalized === "white") return true;
  const hex = normalized.replace("#", "");
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return r > 200 && g > 200 && b > 200;
  }
  if (hex.length === 6) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return r > 200 && g > 200 && b > 200;
  }
  return false;
}

export function LineupsList({
  players,
  shirtColor,
  textColor,
  currentGroupedEventData,
  teamOfficials,
}: LineupsListProps) {
  const playerEvents = currentGroupedEventData?.players;

  // Group players by position and sort by jersey number
  const groupedPlayers = useMemo(() => {
    const map: Record<SoccerPlayerPosition, IPlayers[]> = {} as Record<
      SoccerPlayerPosition,
      IPlayers[]
    >;
    for (const player of players) {
      if (!map[player.position]) map[player.position] = [];
      map[player.position].push(player);
    }
    for (const group of Object.values(map)) {
      group.sort((a, b) => a.jerseyNumber - b.jerseyNumber);
    }
    return map;
  }, [players]);

  const processPlayerEvents = (playerId: string): ProcessedPlayerEvents => {
    const events = playerEvents?.[playerId]?.events || [];

    return events.reduce(
      (acc, event) => {
        if (event.eventType === SoccerMatchEventType.SUSTITUTE) {
          acc.substituteEvent.push(event as ISoccerMatchSubstituteEvent);
        } else {
          acc.otherEvents.push(event);
        }
        return acc;
      },
      {
        substituteEvent: [],
        otherEvents: [],
      } as ProcessedPlayerEvents
    );
  };

  const isLightShirt = isLightColor(shirtColor);
  const shirtStrokeColor = isLightShirt ? "#1936F9" : "white";
  const numberColor = isLightShirt ? "#1936F9" : (textColor || "#fff");

  const PlayerShirt = ({ number }: { number: number }) => (
    <div className="player-shirt">
      <ShirtIcon color={shirtColor} strokeColor={shirtStrokeColor} />
      <span
        className="player-number-overlay"
        style={{ color: numberColor }}
      >
        {number}
      </span>
    </div>
  );

  const PlayerName = ({
    name,
    subInfo,
    isCaptain,
  }: {
    name: string;
    subInfo: ReturnType<typeof getSubstituteDisplayInfo> | null;
    isCaptain?: boolean;
  }) => (
    <div title={name} className="player-name">
      <p>
        {name}
        {isCaptain && (
          <img
            src={captainLogo}
            className="player-captain-logo"
            alt="captain logo"
          />
        )}
      </p>

      {subInfo && (
        <div className="substitute-event">
          <img
            src={substituteImage}
            height={8}
            width={10}
            alt="substitute logo"
          />
          <span className="substitute-time">{subInfo.time}'</span>
          <span title={subInfo.playerName} className="substitute-name">
            {subInfo.playerName}
          </span>
        </div>
      )}
    </div>
  );

  const renderPlayerItem = (player: IPlayers, role: SoccerPlayerPosition) => {
    const { substituteEvent, otherEvents: initialEvents } = processPlayerEvents(
      player.id
    );

    const relevantSubEvent = substituteEvent.find((event) =>
      role === SoccerPlayerPosition.SUBSTITUTE
        ? event.playerOnId === player.id
        : event.playerOffId === player.id
    );

    const displayPlayer = getDisplayPlayerInfo({
      activePlayer: player,
      allPlayers: players,
      substituteEvent: relevantSubEvent,
    });
    const subInfo = getSubstituteDisplayInfo({
      player,
      substituteEvent: relevantSubEvent,
    });

    // If displayPlayer is a different player, fetch their events instead
    const eventsToShow =
      displayPlayer.id !== player.id
        ? processPlayerEvents(displayPlayer.id).otherEvents
        : initialEvents;

    return (
      <div
        key={`${player.jerseyNumber}-${player.name}`}
        className="player-item"
      >
        <PlayerShirt number={displayPlayer.jerseyNumber} />

        <div className="player-info">
          <PlayerName
            name={displayPlayer.name}
            subInfo={subInfo}
            isCaptain={player?.isCaptain}
          />

          <div className="match-events-container">
            {eventsToShow.map((event, idx) => (
              <LineupMatchEvents
                key={`${event.timestamp}-${idx}`}
                event={event}
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderPositionGroup = (position: SoccerPlayerPosition) => {
    const positionPlayers = groupedPlayers[position];

    if (!positionPlayers?.length) {
      return null;
    }

    return (
      <div key={position} className="position-group">
        <div className="position-header">
          <h4 className="position-title">{POSITION_LABELS[position]}</h4>
        </div>
        <div className="players-list">
          {positionPlayers.map((player) => renderPlayerItem(player, position))}
        </div>
      </div>
    );
  };

  const managers = teamOfficials?.filter((o) => o.type === "manager") ?? [];

  return (
    <div className="lineups-list">
      <div className="lineups-list-content">
        {POSITION_ORDER.map(renderPositionGroup)}
        {managers.length > 0 && (
          <div className="position-group">
            <div className="position-header">
              <h4 className="position-title">Manager</h4>
            </div>
            <div className="players-list">
              {managers.map((official) => (
                <div key={official.id} className="player-item">
                  <div className="player-shirt manager-icon">
                    <ManagerIcon color={shirtColor} strokeColor={shirtStrokeColor} />
                  </div>
                  <div className="player-info">
                    <div className="player-name">
                      <p>{official.firstName} {official.lastName}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
